import { useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../services/api';
import { UI, ui } from '../../utils/ui';

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const SHORT_MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

const cellState = {
  available: { bg: '#f0fdf4', border: '#bbf7d0', text: '#166534' },
  booked:    { bg: '#fee2e2', border: '#fca5a5', text: '#991b1b' },
  blocked:   { bg: '#fef3c7', border: '#fcd34d', text: '#92400e' },
  on_hold:   { bg: '#dbeafe', border: '#93c5fd', text: '#1e40af' },
  past:      { bg: '#f9fafb', border: '#e5e7eb', text: '#d1d5db' },
};

const today = () => {
  const d = new Date();
  d.setHours(0,0,0,0);
  return d;
};

const CalendarView = () => {
  const now = new Date();
  const [year, setYear]   = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [data, setData]   = useState(null);
  const [loading, setLoading] = useState(true);

  // Panel state
  const [panel, setPanel] = useState(null); // { roomId, roomName, date, cell }
  const [panelPrice, setPanelPrice]     = useState('');
  const [panelBlocked, setPanelBlocked] = useState(false);
  const [panelNote, setPanelNote]       = useState('');
  const [saving, setSaving]             = useState(false);

  const fetchCalendar = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/calendar?year=${year}&month=${month}`);
      setData(res.data);
    } catch {
      toast.error('Failed to load calendar.');
    } finally {
      setLoading(false);
    }
  }, [year, month]);

  useEffect(() => { fetchCalendar(); }, [fetchCalendar]);

  const prevMonth = () => {
    if (month === 1) { setYear(y => y - 1); setMonth(12); }
    else setMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (month === 12) { setYear(y => y + 1); setMonth(1); }
    else setMonth(m => m + 1);
  };
  const goToday = () => { setYear(now.getFullYear()); setMonth(now.getMonth() + 1); };

  const daysInMonth = new Date(year, month, 0).getDate();
  const todayDate = today();

  const openPanel = (room, dateStr, cell) => {
    const d = new Date(dateStr);
    if (d < todayDate) return; // can't edit past dates
    setPanel({ roomId: room.id, roomName: room.name, date: dateStr, cell });
    setPanelPrice(cell.price != null ? String(cell.price) : String(room.price_per_night));
    setPanelBlocked(cell.state === 'blocked');
    setPanelNote(cell.note || '');
  };

  const closePanel = () => setPanel(null);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/calendar/override', {
        room_id:         panel.roomId,
        start_date:      panel.date,
        price_per_night: panelPrice ? parseFloat(panelPrice) : null,
        is_blocked:      panelBlocked,
        note:            panelNote || null,
      });
      toast.success('Saved.');
      closePanel();
      fetchCalendar();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save.');
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveOverride = async () => {
    setSaving(true);
    try {
      await api.delete('/calendar/override', { data: { room_id: panel.roomId, date: panel.date } });
      toast.success('Override removed.');
      closePanel();
      fetchCalendar();
    } catch {
      toast.error('Failed to remove override.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 300 }}>
      <div style={{ width: 36, height: 36, border: `3px solid ${UI.colors.border}`, borderTopColor: UI.colors.primary, borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );

  const rooms = data?.rooms || [];
  const days = data?.days || {};

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } } .cal-cell:hover { opacity: 0.85; transform: scale(1.03); }`}</style>

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ fontFamily: UI.font.heading, fontSize: 30, fontWeight: 900, margin: 0, color: UI.colors.text }}>Calendar View</h1>
          <p style={{ margin: '6px 0 0', fontSize: 14, color: UI.colors.muted }}>Manage availability and date-wise pricing</p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <button onClick={goToday} style={{ padding: '8px 16px', border: `1.5px solid ${UI.colors.border}`, borderRadius: 10, fontSize: 13, fontWeight: 700, cursor: 'pointer', backgroundColor: '#fff', color: UI.colors.text }}>Today</button>
          <button onClick={prevMonth} style={{ padding: '8px 10px', border: `1.5px solid ${UI.colors.border}`, borderRadius: 10, cursor: 'pointer', backgroundColor: '#fff', display: 'flex' }}><ChevronLeft size={16} /></button>
          <span style={{ fontSize: 16, fontWeight: 800, color: UI.colors.text, minWidth: 160, textAlign: 'center' }}>{MONTHS[month - 1]} {year}</span>
          <button onClick={nextMonth} style={{ padding: '8px 10px', border: `1.5px solid ${UI.colors.border}`, borderRadius: 10, cursor: 'pointer', backgroundColor: '#fff', display: 'flex' }}><ChevronRight size={16} /></button>
        </div>
      </div>

      {/* Legend */}
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: 12 }}>
        {[['available','Available'],['booked','Booked'],['blocked','Blocked'],['on_hold','On Hold']].map(([s, label]) => (
          <div key={s} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <div style={{ width: 12, height: 12, borderRadius: 3, backgroundColor: cellState[s].bg, border: `1.5px solid ${cellState[s].border}` }} />
            <span style={{ color: UI.colors.muted, fontWeight: 600 }}>{label}</span>
          </div>
        ))}
        <span style={{ color: '#9ca3af', fontSize: 11 }}>Click a future cell to set price or block.</span>
      </div>

      {/* Calendar grid */}
      {rooms.length === 0 ? (
        <div style={{ ...ui.card, padding: 40, textAlign: 'center', color: UI.colors.muted }}>No rooms found. Add rooms first.</div>
      ) : (
        <div style={{ ...ui.card, overflowX: 'auto' }}>
          <div style={{ minWidth: rooms.length > 0 ? `${120 + daysInMonth * 52}px` : '100%' }}>
            {/* Header row: dates */}
            <div style={{ display: 'grid', gridTemplateColumns: `120px repeat(${daysInMonth}, 50px)`, gap: 2, padding: '12px 12px 4px', borderBottom: `1px solid ${UI.colors.border}` }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase' }}>Room</div>
              {Array.from({ length: daysInMonth }, (_, i) => {
                const d = i + 1;
                const dateStr = `${year}-${String(month).padStart(2,'0')}-${String(d).padStart(2,'0')}`;
                const isPast = new Date(dateStr) < todayDate;
                const isToday = new Date(dateStr).toDateString() === todayDate.toDateString();
                return (
                  <div key={d} style={{ textAlign: 'center', fontSize: 11, fontWeight: isToday ? 900 : 700, color: isToday ? UI.colors.primary : (isPast ? '#d1d5db' : '#374151') }}>
                    <div>{d}</div>
                    <div style={{ fontSize: 9, color: '#9ca3af' }}>{SHORT_MONTHS[month-1]}</div>
                  </div>
                );
              })}
            </div>

            {/* Room rows */}
            {rooms.map(room => (
              <div key={room.id} style={{ display: 'grid', gridTemplateColumns: `120px repeat(${daysInMonth}, 50px)`, gap: 2, padding: '4px 12px', borderBottom: `1px solid ${UI.colors.border}`, alignItems: 'center' }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: UI.colors.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', paddingRight: 8 }} title={room.name}>
                  {room.name}
                  <div style={{ fontSize: 10, color: '#9ca3af', fontWeight: 500 }}>₹{Number(room.price_per_night).toLocaleString()}</div>
                </div>
                {Array.from({ length: daysInMonth }, (_, i) => {
                  const d = i + 1;
                  const dateStr = `${year}-${String(month).padStart(2,'0')}-${String(d).padStart(2,'0')}`;
                  const cell = days[dateStr]?.[room.id] || { state: 'available', price: room.price_per_night };
                  const isPast = new Date(dateStr) < todayDate;
                  const st = isPast ? cellState.past : (cellState[cell.state] || cellState.available);
                  const hasOverride = cell.state === 'blocked' || (cell.price != null && cell.price !== room.price_per_night);

                  return (
                    <div
                      key={d}
                      className="cal-cell"
                      onClick={() => openPanel(room, dateStr, cell)}
                      title={
                        cell.state === 'booked' ? `${cell.customer_name || ''} (${cell.booking_reference || ''})` :
                        cell.state === 'blocked' ? `Blocked${cell.note ? ': ' + cell.note : ''}` :
                        cell.state === 'on_hold' ? `On hold by ${cell.held_by || ''}` :
                        `₹${Number(cell.price || room.price_per_night).toLocaleString()}`
                      }
                      style={{
                        height: 40,
                        borderRadius: 6,
                        backgroundColor: st.bg,
                        border: `1.5px solid ${st.border}`,
                        cursor: isPast ? 'default' : 'pointer',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'opacity 0.15s, transform 0.15s',
                        position: 'relative',
                        overflow: 'hidden',
                      }}
                    >
                      {cell.state === 'booked' && (
                        <span style={{ fontSize: 9, fontWeight: 700, color: st.text, textAlign: 'center', lineHeight: 1.1, padding: '0 2px', overflow: 'hidden', maxWidth: '100%' }}>
                          {(cell.customer_name || '').split(' ')[0]}
                        </span>
                      )}
                      {cell.state !== 'booked' && (
                        <span style={{ fontSize: 9, fontWeight: 700, color: st.text }}>
                          {cell.state === 'blocked' ? '✕' : cell.state === 'on_hold' ? '⏳' : `₹${Math.round(cell.price || room.price_per_night)}`}
                        </span>
                      )}
                      {hasOverride && !isPast && cell.state !== 'booked' && (
                        <div style={{ position: 'absolute', top: 2, right: 2, width: 5, height: 5, borderRadius: '50%', backgroundColor: UI.colors.primary }} />
                      )}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Side panel overlay */}
      {panel && (
        <>
          <div onClick={closePanel} style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.3)', zIndex: 40 }} />
          <div style={{
            position: 'fixed', right: 0, top: 0, bottom: 0, width: 340,
            backgroundColor: '#ffffff', boxShadow: '-4px 0 24px rgba(0,0,0,0.15)',
            zIndex: 50, display: 'flex', flexDirection: 'column', padding: 24, gap: 20,
            overflowY: 'auto',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <h3 style={{ margin: 0, fontSize: 17, fontWeight: 800, color: UI.colors.text }}>{panel.roomName}</h3>
                <p style={{ margin: '4px 0 0', fontSize: 13, color: UI.colors.muted }}>{panel.date}</p>
              </div>
              <button onClick={closePanel} style={{ border: 'none', background: 'transparent', cursor: 'pointer', padding: 4 }}><X size={18} /></button>
            </div>

            {panel.cell.state === 'booked' ? (
              <div style={{ backgroundColor: '#fee2e2', borderRadius: 10, padding: 16 }}>
                <p style={{ margin: 0, fontWeight: 700, color: '#991b1b' }}>This date is booked</p>
                <p style={{ margin: '6px 0 0', fontSize: 13, color: '#7f1d1d' }}>
                  {panel.cell.customer_name} · {panel.cell.booking_reference}
                </p>
              </div>
            ) : (
              <>
                <div>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: UI.colors.text, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                    Price per night (₹)
                  </label>
                  <input
                    type="number"
                    min={0}
                    value={panelPrice}
                    onChange={e => setPanelPrice(e.target.value)}
                    disabled={panelBlocked}
                    style={{ width: '100%', padding: '10px 12px', borderRadius: 10, border: `1.5px solid ${UI.colors.border}`, fontSize: 14, outline: 'none', boxSizing: 'border-box', opacity: panelBlocked ? 0.5 : 1 }}
                  />
                  <p style={{ fontSize: 11, color: '#9ca3af', margin: '4px 0 0' }}>Leave blank to use room default</p>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <input
                    type="checkbox"
                    id="blocked"
                    checked={panelBlocked}
                    onChange={e => setPanelBlocked(e.target.checked)}
                    style={{ width: 16, height: 16, cursor: 'pointer' }}
                  />
                  <label htmlFor="blocked" style={{ fontSize: 14, fontWeight: 600, color: UI.colors.text, cursor: 'pointer' }}>
                    Block this date (mark unavailable)
                  </label>
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: UI.colors.text, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Note (optional)</label>
                  <input
                    type="text"
                    value={panelNote}
                    onChange={e => setPanelNote(e.target.value)}
                    placeholder="e.g. Maintenance day"
                    style={{ width: '100%', padding: '10px 12px', borderRadius: 10, border: `1.5px solid ${UI.colors.border}`, fontSize: 14, outline: 'none', boxSizing: 'border-box' }}
                  />
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 'auto' }}>
                  <button
                    onClick={handleSave}
                    disabled={saving}
                    style={{ padding: '11px 0', borderRadius: 10, border: 'none', backgroundColor: UI.colors.primary, color: '#fff', fontSize: 14, fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.7 : 1 }}
                  >
                    {saving ? 'Saving…' : 'Save Override'}
                  </button>
                  {(panel.cell.state === 'blocked' || panel.cell.price != null) && (
                    <button
                      onClick={handleRemoveOverride}
                      disabled={saving}
                      style={{ padding: '11px 0', borderRadius: 10, border: `1.5px solid ${UI.colors.border}`, backgroundColor: '#fff', color: UI.colors.text, fontSize: 14, fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer' }}
                    >
                      Remove Override
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default CalendarView;
